import { __awaiter, __rest } from "tslib";
import axios from 'axios';
import axiosRetry from 'axios-retry';
import 'svelte-i18n';
import { Container, Service } from './container';
import { AppState } from '../etc/stores';
axiosRetry(axios, {
    retries: 3,
    retryDelay: retryCount => {
        return retryCount * 1000;
    }
});
export function isHttpError(val) {
    return val.isHttpError;
}
export function extractSlugFromError(e) {
    var _a;
    if (isHttpError(e)) {
        return e.res.body.slug;
    }
    else if ((_a = e.response) === null || _a === void 0 ? void 0 : _a.data) {
        return e.response.data.slug;
    }
    return e.message;
}
export function extractMessageFromError(e) {
    var _a, _b;
    if (isHttpError(e)) {
        return Object.values(((_b = (_a = e.res.body.message) === null || _a === void 0 ? void 0 : _a[0]) === null || _b === void 0 ? void 0 : _b.constraints) || { error: e.message })[0];
    }
    return e.message;
}
axios.defaults.withCredentials = true;
export class Api extends Service {
    constructor(a) {
        super(a);
        this.baseUrl = '';
        this.setAuthIntercept();
    }
    setAuthIntercept() {
        return __awaiter(this, void 0, void 0, function* () {
            axios.interceptors.response.use(undefined, (error) => __awaiter(this, void 0, void 0, function* () {
                var _a, _b, _c, _d, _e;
                if (((_a = error.response) === null || _a === void 0 ? void 0 : _a.status) === 401 &&
                    ((_e = (_d = (_c = (_b = error.response.data) === null || _b === void 0 ? void 0 : _b.message) === null || _c === void 0 ? void 0 : _c[0]) === null || _d === void 0 ? void 0 : _d.constraints) === null || _e === void 0 ? void 0 : _e.UNAUTHORIZED)) {
                    this.resetWidgetWhenUnauthorized();
                }
                // Always reject: returning a value here makes axios resolve the request
                // to `undefined`, which then throws in handleResponse.
                return Promise.reject(error);
            }));
        });
    }
    handleResponse(request, response) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!response) {
                throw Object.assign(new Error('No response received'), {
                    res: {
                        body: '{}',
                        status: 0,
                        message: 'No response received',
                        headers: {}
                    },
                    req: request,
                    isHttpError: true
                });
            }
            const res = {
                body: response.data || (response.json && (yield response.json())) || '{}',
                status: response.status,
                message: response.statusText,
                headers: response.headers
            };
            if (response.status < 400) {
                return res;
            }
            throw Object.assign(new Error(response.statusText), {
                res,
                req: request,
                isHttpError: true
            });
        });
    }
    createRequest(method, path, params) {
        const url = this.baseUrl + path + this.stringifyQuery(params.query);
        const { body, headers } = params, info = __rest(params, ["body", "headers"]);
        return {
            url,
            props: Object.assign(Object.assign({}, info), { method, headers: Object.assign(Object.assign({ 'Content-Type': 'application/json', 'event-platform': 'widget' }, (this.token ? { Authorization: this.token } : {})), (headers !== null && headers !== void 0 ? headers : {})), body: body ? JSON.stringify(body) : undefined })
        };
    }
    create(baseUrl) {
        const api = new Api(this.ctx);
        api.baseUrl = baseUrl;
        return api;
    }
    setBearerToken(token) {
        this.token = `Bearer ${token}`;
        return this;
    }
    setAuth(token) {
        this.token = token;
        return this;
    }
    makeRequest(call, req) {
        return __awaiter(this, void 0, void 0, function* () {
            try {
                const res = yield call();
                return res;
            }
            catch (e) {
                if (e.code === 'ERR_NETWORK') {
                    const response = yield fetch(req.url, req.props);
                    return response;
                }
                throw e;
            }
        });
    }
    get(path, params = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const req = this.createRequest('get', path, params);
            const res = yield this.makeRequest(() => axios.get(req.url, req.props), req);
            return this.handleResponse(req, res);
        });
    }
    post(path, params = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const req = this.createRequest('post', path, params);
            const res = yield this.makeRequest(() => axios.post(req.url, req.props.body, req.props), req);
            return this.handleResponse(req, res);
        });
    }
    put(path, params = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const req = this.createRequest('put', path, params);
            const res = yield this.makeRequest(() => axios.put(req.url, req.props.body, req.props), req);
            return this.handleResponse(req, res);
        });
    }
    patch(path, params = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const req = this.createRequest('patch', path, params);
            const res = yield this.makeRequest(() => axios.patch(req.url, req.props.body, req.props), req);
            return this.handleResponse(req, res);
        });
    }
    delete(path, params = {}) {
        return __awaiter(this, void 0, void 0, function* () {
            const req = this.createRequest('delete', path, params);
            const res = yield this.makeRequest(() => axios.delete(req.url, req.props), req);
            return this.handleResponse(req, res);
        });
    }
    resetWidgetWhenUnauthorized() {
        AppState.update(state => (Object.assign(Object.assign({}, state), { auth: Object.assign(Object.assign({}, state.auth), { isLogout: true, connected: false, access_token: undefined }) })));
    }
    stringifyQuery(query) {
        if (!query)
            return '';
        const args = [];
        Object.keys(query).map(key => {
            const value = query[key];
            if (value) {
                args.push(`${encodeURIComponent(key)}=${encodeURIComponent(value)}`);
            }
        });
        return `?${args.join('&')}`;
    }
}
Container.add(Api);
